import type { BadgeTone } from '@/components/ui/badge'
import type { PlanDistributionItem } from '@/services/revenue'

/** Solo, Agency and retired paid plans; not Free or "Not selected". */
export const isPaidPlan = (item: PlanDistributionItem) =>
    item.billingInterval === 'monthly' || item.billingInterval === 'yearly'

/** At least one of the plan's agencies is billed on a price that is on record. */
export const hasKnownPrices = (item: PlanDistributionItem) =>
    item.prices.some((price) => price.monthlyAmount !== null)

/**
 * A plan's MRR as the card shows it: its price rows' exact cents, added in cents.
 * The API's per-plan total is a float sum rounded half to even, so it can be $1
 * off a lone price row ($346.50 comes back as 346 while the row rounds to $347).
 */
export function planMrrOf(item: PlanDistributionItem): number {
    if (item.prices.length === 0) return item.revenueContribution
    return item.prices.reduce((cents, price) => cents + Math.round(price.revenue * 100), 0) / 100
}

export interface PlanDistributionSummary {
    totalAgencies: number
    /** Agencies on a paid plan, trials included. */
    paidPlanAgencies: number
    totalMrr: number
    /** Active subscriptions on a paid plan. */
    payingAgencies: number
    /** Active subscriptions on a known price other than their plan's current one. */
    olderPriceAgencies: number
    /** What those would add per month at their plan's current price. */
    olderPriceMonthlyGap: number
}

export function summarizePlanDistribution(items: PlanDistributionItem[]): PlanDistributionSummary {
    const summary: PlanDistributionSummary = {
        totalAgencies: 0,
        paidPlanAgencies: 0,
        totalMrr: 0,
        payingAgencies: 0,
        olderPriceAgencies: 0,
        olderPriceMonthlyGap: 0,
    }
    for (const item of items) {
        summary.totalAgencies += item.agencyCount
        summary.totalMrr += planMrrOf(item)
        if (!isPaidPlan(item)) continue
        summary.paidPlanAgencies += item.agencyCount
        for (const price of item.prices) {
            summary.payingAgencies += price.activeCount
            // Comparing needs both amounts: a known price, and a current one on the plan.
            if (price.monthlyAmount === null || price.isCurrent || item.currentMonthlyAmount === null) continue
            summary.olderPriceAgencies += price.activeCount
            summary.olderPriceMonthlyGap += (item.currentMonthlyAmount - price.monthlyAmount) * price.activeCount
        }
    }
    return summary
}

export type PricePartKind = 'current' | 'older' | 'unknown' | 'solid'

export interface PricePart {
    kind: PricePartKind
    agencies: number
    mrr: number
}

/**
 * How a plan's bar segment splits: current price, then every older price as one
 * part, then prices not on record. A plan with no known price is one solid part.
 */
export function pricePartsOf(item: PlanDistributionItem): PricePart[] {
    if (!hasKnownPrices(item)) {
        return [{ kind: 'solid', agencies: item.agencyCount, mrr: planMrrOf(item) }]
    }
    const current: PricePart = { kind: 'current', agencies: 0, mrr: 0 }
    const older: PricePart = { kind: 'older', agencies: 0, mrr: 0 }
    const unknown: PricePart = { kind: 'unknown', agencies: 0, mrr: 0 }
    for (const price of item.prices) {
        const part = price.monthlyAmount === null ? unknown : price.isCurrent ? current : older
        part.agencies += price.agencyCount
        part.mrr += price.revenue
    }
    return [current, older, unknown].filter((part) => part.agencies > 0)
}

/** Share as a whole percent: "<1%" for a small non-zero share, ">99%" for a partial one that would round to 100%. */
export function formatShare(count: number, total: number): string {
    if (count <= 0 || total <= 0) return '0%'
    const percent = (count / total) * 100
    if (percent < 1) return '<1%'
    if (percent > 99 && count < total) return '>99%'
    return `${Math.round(percent)}%`
}

const SUBSCRIPTION_STATUSES: Record<string, { label: string; tone: BadgeTone }> = {
    active: { label: 'Active', tone: 'success' },
    trialing: { label: 'Trialing', tone: 'info' },
    past_due: { label: 'Past due', tone: 'warning' },
    unpaid: { label: 'Unpaid', tone: 'warning' },
    incomplete: { label: 'Incomplete', tone: 'attention' },
    canceled: { label: 'Canceled', tone: 'danger' },
}

/** An agency's Stripe status as a badge. Null is a paid plan with no subscription behind it. */
export function subscriptionStatusOf(status: string | null): { label: string; tone: BadgeTone } {
    if (status === null) return { label: 'No subscription', tone: 'neutral' }
    return SUBSCRIPTION_STATUSES[status] ?? { label: status, tone: 'neutral' }
}
